import axiosInstance, { API_CONFIG } from "config/apiConfig";
import { Comment } from "models/Comment";

const COMMENTS_URL = `${API_CONFIG.baseURL}/comments`;

interface CommentCreate {
  user_id: string;  // from_user in API
  funding_opp_mapped_id: string;
  comment: string;
  to_users?: string[];  // Optional list of user IDs
}

interface CommentResponse {
  comment_id: number;
  from_user: string;
  to_users: string[];
  funding_opp_mapped_id: number;
  comment: string;
  created_at: string;
  updated_at: string;
}

const mapComment = (comment: CommentResponse): Comment => ({
  comment: comment.comment,
  commentId: comment.comment_id.toString(),
  fundingOppMappedId: comment.funding_opp_mapped_id.toString(),
  userId: comment.from_user,
  toUsers: comment.to_users,
  createdAt: comment.created_at,
  updatedAt: comment.updated_at,
});

const mapComments = (comments: CommentResponse[]): Comment[] => {
  if (!Array.isArray(comments)) return [];
  
  return comments
    .sort((a: CommentResponse, b: CommentResponse) => 
      new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
    )
    .map(mapComment);
};

export class CommentService {
  static async getCommentsByOpportunityId(fundingOppMappedId: string): Promise<Comment[]> {
    try {
      const response = await axiosInstance.get(`${COMMENTS_URL}/funding_opp/${fundingOppMappedId}`);
      // The response is an array where the first element contains the data and message
      const responseWrapper = response.data[0];
      const comments = responseWrapper?.data;
      
      if (!comments) {
        console.warn('No comments data received from API');
        return [];
      }
      
      return mapComments(comments);
    } catch (error) {
      console.error('Failed to fetch comments:', error);
      throw error;
    }
  }

  static async getCommentById(commentId: string): Promise<Comment> {
    const response = await axiosInstance.get(`${COMMENTS_URL}/${commentId}`);
    // The response is an array where the first element contains the data and message
    const responseWrapper = response.data[0];
    const comment = responseWrapper?.data;
    
    if (!comment) {
      throw new Error('No comment data received from API');
    }
    return mapComment(comment);
  }

  static async createComment(comment: CommentCreate): Promise<Comment> {
    try {
      const response = await axiosInstance.post(COMMENTS_URL, {
        user_id: comment.user_id,
        funding_opp_mapped_id: parseInt(comment.funding_opp_mapped_id),
        comment: comment.comment,
        to_users: comment.to_users || []
      });

      // Handle both possible response structures
      const responseData = Array.isArray(response.data) ? response.data[0].data : response.data.data;
      
      if (!responseData) {
        throw new Error('No comment data received from API');
      }

      return mapComment(responseData);
    } catch (error) {
      console.error('Failed to create comment:', error);
      throw error;
    }
  }

  static async getCommentCount(fundingOppMappedId: string): Promise<number> {
    try {
      const response = await axiosInstance.get(`${COMMENTS_URL}/count/${fundingOppMappedId}`);
      // The response is an array where the first element contains the data and message
      const responseWrapper = response.data[0];
      return responseWrapper?.data || 0;
    } catch (error) {
      console.error('Failed to fetch comment count:', error);
      return 0;
    }
  }
} 